import React from "react";
import { FiSearch } from "react-icons/fi";
import { RxCross2 } from "react-icons/rx";
import { IoWarningOutline } from "react-icons/io5";
import { PatientSearchState, PatientSearchActions } from "../types";

interface PatientSearchFiltersProps {
  state: PatientSearchState;
  actions: PatientSearchActions;
}

const PatientSearchFilters: React.FC<PatientSearchFiltersProps> = ({
  state,
  actions,
}) => {
  const {
    showSearch,
    selectedFacility,
    searchDob,
    patientSearchLoader,
    searchError,
    selectedPatient,
    facilities,
  } = state;

  const {
    setShowSearch,
    setSelectedFacility,
    setSearchDob,
    setSelectedPatient,
    clearSearchError,
    handleSearchClick,
    handleClearFilters,
  } = actions;

  const hasFilters = showSearch || selectedFacility || searchDob;

  return (
    <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="flex gap-4 items-end flex-wrap">
        {/* Search Bar */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Search
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name or MRN..."
              value={showSearch}
              onChange={(e) => {
                setShowSearch(e.target.value);
                clearSearchError();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearchClick();
              }}
              className="w-full h-[40px] border border-gray-300 rounded-lg px-3 pr-10 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
            />
            {showSearch ? 
              <RxCross2
                className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 cursor-pointer"
                size={16}
                onClick={() => setShowSearch("")}
              />
              : 
              <FiSearch
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={16}
              /> 
            }
          </div>
        </div>

        {/* Location Dropdown */}
        <div className="min-w-[200px]">
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Location
          </label>
          <select
            value={selectedFacility}
            onChange={(e) => {
              setSelectedFacility(e.target.value);
              clearSearchError();
            }}
            className="w-full h-[40px] border border-gray-300 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm bg-white cursor-pointer"
          >
            <option value="">All Locations</option>
            {facilities?.map((item: any, index: number) => (
              <option key={index} value={item?.id}>
                {item?.facilityName || item?.name || item}
              </option>
            ))}
          </select>
        </div>

        {/* Date of Birth */}
        <div className="min-w-[180px]">
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Date of Birth
          </label>
          <input
            type="text"
            placeholder="Select DoB"
            onFocus={(e) => (e.target.type = "date")}
            onBlur={(e) => {
              if (!e.target.value) e.target.type = "text";
            }}
            max={new Date().toISOString().split("T")[0]}
            value={searchDob}
            onChange={(e) => {
              setSearchDob(e.target.value);
              clearSearchError();
            }}
            className="w-full h-[40px] border border-gray-300 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
          />
        </div>

        {/* Search Button */}
        <button
          onClick={handleSearchClick}
          disabled={patientSearchLoader}
          className="h-[40px] px-5 text-white text-sm font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed rounded-lg transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
        >
          {patientSearchLoader ? (
            <>
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                ></path>
              </svg>
              Searching...
            </>
          ) : (
            <>
              <FiSearch size={14} />
              Search
            </>
          )}
        </button>

        {/* Clear Filters */}
        {hasFilters && (
          <button
            onClick={handleClearFilters}
            className="h-[40px] px-4 text-red-600 text-sm font-semibold hover:bg-red-50 rounded-lg border border-red-200 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Validation / Error Message */}
      {searchError && (
        <div className="mt-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <IoWarningOutline className="text-red-500 flex-shrink-0" size={16} />
          <span className="text-sm text-red-600">{searchError}</span>
        </div>
      )}

      {/* Selected Patient Indicator */}
      {selectedPatient && (
        <div className="mt-3 flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2">
          <span className="text-sm font-medium text-teal-800">
            Selected: {selectedPatient?.lastName}, {selectedPatient?.firstName}
            {selectedPatient?.birthDate
              ? ` | DoB: ${selectedPatient.birthDate}`
              : ""}
            {selectedPatient?.location
              ? ` | ${selectedPatient.location}`
              : ""}
          </span>
          <button
            onClick={() => setSelectedPatient(null)}
            className="ml-auto text-xs text-red-600 font-semibold hover:underline"
          >
            Change
          </button>
        </div>
      )}
    </div>
  );
};

export default PatientSearchFilters;